import { useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight, Calendar, X } from "lucide-react";
import { useState } from "react";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const MONTH_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export function CustomMonthPicker({ value, onChange, min, max, placeholder = "Select month", className = "" }) {
  const dialogRef = useRef(null);
  const [viewYear, setViewYear] = useState(() => {
    if (value) return parseInt(value.slice(0, 4));
    return new Date().getFullYear();
  });

  const openDialog = useCallback(() => {
    if (value) setViewYear(parseInt(value.slice(0, 4)));
    dialogRef.current?.showModal();
  }, [value]);

  const closeDialog = useCallback(() => {
    dialogRef.current?.close();
  }, []);

  const handleSelect = useCallback((monthIdx) => {
    const key = `${viewYear}-${String(monthIdx + 1).padStart(2, "0")}`;
    onChange(key);
    closeDialog();
  }, [viewYear, onChange, closeDialog]);

  const isDisabled = useCallback((monthIdx) => {
    const key = `${viewYear}-${String(monthIdx + 1).padStart(2, "0")}`;
    if (min && key < min) return true;
    if (max && key > max) return true;
    return false;
  }, [viewYear, min, max]);

  const isSelected = useCallback((monthIdx) => {
    if (!value) return false;
    return value === `${viewYear}-${String(monthIdx + 1).padStart(2, "0")}`;
  }, [value, viewYear]);

  const canGoPrev = !min || viewYear > parseInt(min.slice(0, 4));
  const canGoNext = !max || viewYear < parseInt(max.slice(0, 4));

  const displayText = value
    ? `${MONTH_FULL[parseInt(value.slice(5, 7)) - 1]} ${value.slice(0, 4)}`
    : placeholder;

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className={`flex items-center gap-2 h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm hover:border-slate-300 transition-colors w-full text-left ${className}`}
      >
        <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
        <span className={value ? "text-slate-900 font-medium" : "text-slate-400"}>
          {displayText}
        </span>
      </button>

      <dialog
        ref={dialogRef}
        className="fixed inset-0 m-auto w-[300px] rounded-2xl border-0 shadow-2xl bg-white p-0 backdrop:bg-black/30"
        onClick={(e) => { if (e.target === dialogRef.current) closeDialog(); }}
      >
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => canGoPrev && setViewYear(y => y - 1)}
              disabled={!canGoPrev}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-slate-600" />
            </button>
            <span className="text-base font-semibold text-slate-800">{viewYear}</span>
            <button
              type="button"
              onClick={() => canGoNext && setViewYear(y => y + 1)}
              disabled={!canGoNext}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-slate-600" />
            </button>
          </div>

          {/* Month Grid */}
          <div className="grid grid-cols-3 gap-2">
            {MONTHS.map((name, idx) => {
              const disabled = isDisabled(idx);
              const selected = isSelected(idx);
              return (
                <button
                  key={idx}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleSelect(idx)}
                  className={`
                    h-10 rounded-xl text-sm font-medium transition-all
                    ${selected
                      ? "bg-slate-800 text-white shadow-md"
                      : disabled
                        ? "text-slate-300 cursor-not-allowed"
                        : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                    }
                  `}
                >
                  {name}
                </button>
              );
            })}
          </div>

          {/* Clear / Close */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
            {value && (
              <button
                type="button"
                onClick={() => { onChange(""); closeDialog(); }}
                className="text-xs text-slate-400 hover:text-red-500 flex items-center gap-1 transition-colors"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            )}
            <button
              type="button"
              onClick={closeDialog}
              className="ml-auto text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
}
